import {
  createContext, useContext, useEffect, ReactNode, useState,
} from 'react';
import { Team } from 'lib/types/teams';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { notify } from 'lib/utils/honeybadgerUtils';

type ProviderProps = { children: ReactNode };

/**
 * Return either an array of teams or undefined.
 * Undefined means that the teams are not loaded yet.
 **/
const TeamsContext = createContext<Team[] | undefined>(undefined);

/** Returns the teams where the user is an active member or manager */
function useTeams() {
  return useContext(TeamsContext);
}

function TeamsProvider({ children }: ProviderProps) {
  const [teams, setTeams] = useState<Team[] | undefined>();

  const API = useApi();
  const currentUser = useCurrentUser();

  useEffect(() => {
    const getTeams = async () => {
      if (!currentUser) {
        return;
      }

      const maxRetries = 3;
      let retryCount = 0;
      let retryDelay = 2000; // 2-second delay

      /** Use exponential backoff to retry fetching teams */
      const attemptGetTeams = async (): Promise<Team[]> => {
        try {
          const data = await API.getTeams();
          return data || [];
        } catch (err) {
          console.error('Teams fetch failed:', err);

          if (retryCount < maxRetries) {
            retryCount += 1;
            console.log(`Retrying teams fetch (${retryCount}/${maxRetries}) in ${retryDelay}ms...`);

            await new Promise((resolve) => setTimeout(resolve, retryDelay));

            retryDelay *= 2;

            return attemptGetTeams();
          }

          console.error('Teams fetch failed after all retry attempts');
          notify(
            new Error('Teams fetch failed after all retry attempts'),
            'lib/contexts/teamsContext',
            {
              retryCount,
              retryDelay,
            },
          );
          return [];
        }
      };

      try {
        const data = await attemptGetTeams();
        setTeams(data);
      } catch (err) {
        console.error('Fatal error during teams fetch:', err);
        setTeams([]);
      }
    };

    if (!teams) getTeams();
  }, [currentUser, API, teams]);

  return (
    <TeamsContext.Provider value={teams}>
      {children}
    </TeamsContext.Provider>
  );
}

export { TeamsProvider, useTeams };
